import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { RouterNavigation } from '@ngxs/router-plugin';
import { Action, Actions, InitState, ofActionSuccessful, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { duration } from 'moment';
import { combineLatest, merge } from 'rxjs';
import { auditTime, filter, mergeMap, switchMap, take } from 'rxjs/operators';
import { AuthenticationSettingsSelectors } from 'src/app/store/authentication-settings/authentication-settings.selectors';
import { AuthenticationSettingsStateModel } from 'src/app/store/authentication-settings/authentication-settings.state';
import { SamwinAcdExtensionStateSelectors } from 'src/app/store/samwin/samwin-acd-extension/samwin-acd-extension.selectors';
import { SessionsService } from 'src/generated/swagger';
import { AuthenticateWithMicrosoftEntraIdToken, AuthenticateWithWindowsAuthentication } from './authentication/authentication.actions';
import { AuthenticationState, AuthenticationStateModel } from './authentication/authentication.state';
import { EnabledFeaturesState } from './enabled-features/enabled-features.state';
import {
   CloseSessionControlDialog,
   SessionRelevantDataChanged,
   SessionUpdated,
   TerminateAllUserSessions,
   TerminateCurrentSession,
   ToggleSessionControlDialog,
   UserInformationChanged,
} from './session.actions';

export interface SessionStateModel {
   isSignOutDialogOpen: boolean;
   isAuthenticated: boolean;
   canChangePasswordForAccount: boolean;
   user: SessionUserInfo;
}

@State<SessionStateModel>({
   name: 'session',
   defaults: {
      isSignOutDialogOpen: false,
      isAuthenticated: false,
      canChangePasswordForAccount: false,
      user: {
         userId: undefined,
         firstname: undefined,
         lastname: undefined,
         userName: undefined,
         displayName: undefined,
         dn: undefined,
         email: undefined,
         emailConnectorId: undefined,
         userStateNotificationwindowInterval: undefined,
         userStateNotificationwindowRepeat: undefined,
         userStateNotificationwindowTreatDeactivatedAsReachable: undefined,
      },
   },
   children: [EnabledFeaturesState, AuthenticationState],
})
@Injectable()
export class SessionState {
   constructor(private router: Router, private store: Store, private actions$: Actions, private sessionsService: SessionsService) {
      this.authenticateIf(
         AuthenticationSettingsSelectors.useWindowsAuthentication,
         () => null,
         () => new AuthenticateWithWindowsAuthentication()
      );
      this.authenticateIf(
         AuthenticationSettingsSelectors.performAutoMicrosoftEntraIdAuthentication,
         AuthenticationSettingsSelectors.getMicrosoftEntraIdAuthenticationSettings,
         (settings) => new AuthenticateWithMicrosoftEntraIdToken(settings)
      );

      merge(this.actions$.pipe(ofActionSuccessful(InitState)), this.actions$.pipe(ofActionSuccessful(RouterNavigation)))
         .pipe(
            auditTime(100),
            mergeMap(() =>
               combineLatest([this.store.select(SessionState.getIsAuthenticated), this.store.select(SamwinAcdExtensionStateSelectors.isAcdExtensionAssigned)])
            )
         )
         .subscribe(([isAuthenticated, isAcdExtensionAssigned]) => {
            if (isAuthenticated && isAcdExtensionAssigned) {
               this.router.navigate(['dashboard']);
               return;
            }
            if (isAuthenticated && !isAcdExtensionAssigned) {
               this.router.navigate(['authentication/acdSelection']);
               return;
            }
            this.router.navigate(['authentication/authorization']);
         });
   }

   @Selector([SessionState])
   public static getIsSignOutDialogOpen(state: SessionStateModel) {
      return state.isSignOutDialogOpen;
   }

   @Selector([SessionState])
   public static getIsAuthenticated(state: SessionStateModel) {
      return state.isAuthenticated;
   }

   @Selector([SessionState])
   public static canChangePassword(state: SessionStateModel) {
      return state.canChangePasswordForAccount;
   }

   @Selector([SessionState])
   public static getAuthenticatedUser(state: SessionStateModel): SessionUserInfo {
      return state.user;
   }

   @Selector([SessionState])
   public static getAuthenticatedUserId(state: SessionStateModel) {
      if (state.isAuthenticated) {
         return state.user.userId;
      }
      return null;
   }

   @Selector([SessionState])
   public static getAuthenticatedUserMail(state: SessionStateModel) {
      if (state.isAuthenticated) {
         return state.user.email;
      }
      return null;
   }

   @Selector([SessionState])
   public static getUserFullname(state: SessionStateModel) {
      if (_.isNil(state.user)) {
         return '';
      }
      if (!_.isEmpty(state.user.displayName)) {
         return state.user.displayName;
      }
      if (!_.isEmpty(state.user.userName)) {
         return state.user.userName;
      }
      return '';
   }

   @Selector([SessionState])
   public static getUserDn(state: SessionStateModel) {
      return _.isNil(state.user.dn) ? '' : state.user.dn;
   }

   @Selector([SessionState])
   public static getUserStateNotificationwindowInterval(state: SessionStateModel): moment.Duration {
      const intervalAsSeconds = _.isNil(state.user.userStateNotificationwindowInterval) ? 0 : state.user.userStateNotificationwindowInterval;
      return duration(intervalAsSeconds, 'seconds');
   }

   @Selector([SessionState])
   public static getUserStateNotificationwindowRepeat(state: SessionStateModel): boolean {
      return _.isNil(state.user.userStateNotificationwindowRepeat) ? false : state.user.userStateNotificationwindowRepeat;
   }

   @Selector([SessionState])
   public static getUserStateNotificationwindowTreatDeactivatedAsReachable(state: SessionStateModel): boolean {
      return _.isNil(state.user.userStateNotificationwindowTreatDeactivatedAsReachable)
         ? false
         : state.user.userStateNotificationwindowTreatDeactivatedAsReachable;
   }

   @Action(UserInformationChanged)
   public userInformationChanged(ctx: StateContext<SessionStateModel>, action: UserInformationChanged) {
      if (_.isNil(action.userInformationData) || _.isNil(action.userInformationData.userShortInfo)) {
         ctx.patchState({
            user: {
               userId: ctx.getState().user.userId,
               userName: undefined,
               firstname: undefined,
               lastname: undefined,
               displayName: undefined,
               dn: undefined,
               email: undefined,
               emailConnectorId: undefined,
               emailMaximumSize: undefined,
               userStateNotificationwindowInterval: undefined,
               userStateNotificationwindowRepeat: undefined,
            },
         });
         return;
      }
      ctx.patchState({
         user: {
            userId: action.userInformationData.userShortInfo.id,
            userName: action.userInformationData.userShortInfo.username,
            firstname: action.userInformationData.userShortInfo.firstname,
            lastname: action.userInformationData.userShortInfo.lastname,
            displayName: action.userInformationData.userShortInfo.displayName,
            dn: action.userInformationData.userShortInfo.dn,
            email: action.userInformationData.userShortInfo.email,
            emailMaximumSize: action.userInformationData.userConfig.emailMaximumSize,
            emailConnectorId: action.userInformationData.userConfig.emailConnectorId,
            userStateNotificationwindowInterval: action.userInformationData.userConfig.userStateNotificationwindowInterval,
            userStateNotificationwindowRepeat: action.userInformationData.userConfig.userStateNotificationwindowRepeat,
            userStateNotificationwindowTreatDeactivatedAsReachable:
               action.userInformationData.userConfig.userStateNotificationwindowTreatDeactivatedAsReachable,
         },
      });

      ctx.dispatch(new SessionRelevantDataChanged());
   }

   @Action(SessionUpdated)
   public sessionUpdated(ctx: StateContext<SessionStateModel>, action: SessionUpdated) {
      ctx.patchState({
         isAuthenticated: action.sessionInfo.isAuthenticated,
         canChangePasswordForAccount: action.sessionInfo.canChangePasswordForAccount,
         user: {
            userId: action.sessionInfo.userId,
         },
      });

      ctx.dispatch(new SessionRelevantDataChanged());
   }

   @Action(ToggleSessionControlDialog)
   public toggleSessionControlDialog(ctx: StateContext<SessionStateModel>, action: ToggleSessionControlDialog) {
      ctx.patchState({
         isSignOutDialogOpen: !ctx.getState().isSignOutDialogOpen,
      });
   }

   @Action(CloseSessionControlDialog)
   public cseSessionControlDialog(ctx: StateContext<SessionStateModel>, action: CloseSessionControlDialog) {
      ctx.patchState({
         isSignOutDialogOpen: false,
      });
   }

   @Action(TerminateCurrentSession)
   public terminateCurrentSession(ctx: StateContext<AuthenticationStateModel>, action: TerminateCurrentSession) {
      return this.sessionsService.terminateActiveSession();
   }

   @Action(TerminateAllUserSessions)
   public terminateAllUserSessions(ctx: StateContext<AuthenticationStateModel>, action: TerminateAllUserSessions) {
      return this.sessionsService.terminateAllSessionsForUser();
   }

   private authenticateIf<AuthSettingsType>(
      getAuthenticationCondition: (state: AuthenticationSettingsStateModel) => boolean,
      getAuthenticationSettings: (state: AuthenticationSettingsStateModel) => AuthSettingsType,
      authenticateAction: (settings: AuthSettingsType) => any
   ): void {
      this.actions$
         .pipe(
            ofActionSuccessful(InitState),
            switchMap(() =>
               combineLatest([
                  this.store.select(getAuthenticationCondition),
                  this.store.select(getAuthenticationSettings),
                  this.store.select(SessionState.getIsAuthenticated),
               ])
            ),
            filter(([authenticationCondition, settings, isAuthenticated]) => !isAuthenticated && authenticationCondition),
            switchMap(([authenticationCondition, settings, isAuthenticated]) => this.store.dispatch(authenticateAction(settings))),
            take(1)
         )
         .subscribe();
   }
}

export interface SessionUserInfo {
   userId: number;
   userName?: string;
   firstname?: string;
   lastname?: string;
   displayName?: string;
   dn?: string;
   email?: string;
   emailConnectorId?: number;
   emailMaximumSize?: number;
   userStateNotificationwindowInterval?: number;
   userStateNotificationwindowRepeat?: boolean;
   userStateNotificationwindowTreatDeactivatedAsReachable?: boolean;
}
